import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { Outlet, Link, useLocation, useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@auth/presentation/hooks/useAuth';
import { Badge, Button, ConfirmModal, LoadingSpinner, Tour, TutorialsMenu, useOnboardingTour, useToast } from '@klonup/design-system';
import type { TourLabels, TutorialsMenuLabels } from '@klonup/design-system';
import { useMyOrganization } from '@shared/organizations/presentation/hooks/useMyOrganization';
import { OrganizationDependencyContainer } from '@shared/organizations/infrastructure/dependencies/OrganizationDependencyContainer';
import type { Organization } from '@shared/organizations/domain/entities/Organization';
import { UpgradePlanBadge } from '@organization/layout/UpgradePlanBadge';
import { inactivityBanner } from '@organization/layout/inactivityBanner';
import { scheduledPlanChangeBanner } from '@organization/layout/scheduledPlanChangeBanner';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { ContactEmailLink } from '@/shared/components/ContactEmailLink';
import ProfileDropdown from '@shared/components/ProfileDropdown';
import logotipo from '@klonup/logos/RGB - Logotipo Klonup.svg';
import { useWelcomeAdminTour } from '@shared/onboarding/presentation/tours/useWelcomeAdminTour';
import { useCreateConnectionTour } from '@shared/onboarding/presentation/tours/useCreateConnectionTour';
import { useBackupManagerWelcomeTour } from '@shared/onboarding/presentation/tours/useBackupManagerWelcomeTour';
import { useBackupManagerConnectionDetailTour } from '@shared/onboarding/presentation/tours/useBackupManagerConnectionDetailTour';
import { useAdminConnectionDetailTour } from '@shared/onboarding/presentation/tours/useAdminConnectionDetailTour';
import { useAdminCreateTaskTour } from '@shared/onboarding/presentation/tours/useAdminCreateTaskTour';
import { OnboardingDependencyContainer } from '@shared/onboarding/infrastructure/dependencies/OnboardingDependencyContainer';
import { setTourActive } from '@shared/onboarding/tourActiveFlag';
import { orgPaths } from '@shared/routes/appPaths';

// Tours que se auto-disparan vía evento (klonup:start-tour) y por tanto deben
// respetar la guarda hasCompleted. Los demás (relanzamiento manual desde el menú)
// se inician siempre, ignorando la guarda.
const AUTO_TRIGGERED_BY_EVENT = ['bm-connection-detail', 'admin-connection-detail'];

// Tours cuyas anclas solo existen en el detalle de una conexión (ruta dinámica /connections/:id).
// No pueden usar requiredPath (no se sabe qué conexión abrir), así que se deshabilitan en el
// menú fuera de esa ruta. El motor del DS es agnóstico: recibe qué deshabilitar, no el porqué.
const TOURS_REQUIRING_KLON_DETAIL = ['bm-connection-detail', 'admin-connection-detail', 'admin-create-task'];

const OrgHeader: React.FC<{
  allToursCompleted: boolean;
  organization: Organization | null;
}> = ({ allToursCompleted, organization }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const prefix = orgPaths.root(slug ?? '');
  const openTutorials = () => window.dispatchEvent(new CustomEvent('klonup:open-tutorials-menu'));

  const isAdmin = user?.role === 'admin';
  // D2/D8: el badge-morph interactivo solo aparece si el caller es Admin y la organización
  // está activa. En cualquier otro caso (BackupManager, org inactiva) se muestra el Badge
  // estático de siempre.
  const canShowUpgrade = organization !== null && isAdmin && organization.isActive();

  return (
    <header style={{ height: '72px', backgroundColor: 'white', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

        <div className="flex items-center gap-6">
          <Link to={`${prefix}/my-organization`} className="flex items-center">
            <img src={logotipo} alt="Klonup" style={{ height: '54px', width: 'auto' }} />
          </Link>

          {/* Bloque de identidad de la organización (D1): solo con organization cargada, sin
              skeleton mientras carga. Ancla del paso `intro` del tour welcome-admin (D4). */}
          {organization !== null && (
            <>
              <div aria-hidden="true" style={{ width: '1px', height: '32px', backgroundColor: '#E5E7EB' }} />
              <div data-tour="org-identity">
                <p style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '15px', color: 'var(--color-klonup-indigo)', margin: 0, lineHeight: 1.3 }}>
                  {organization.name}
                </p>
                <p style={{ fontSize: '12px', color: '#94A3B8', margin: 0, lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span
                    aria-hidden="true"
                    style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: organization.isActive() ? '#10b981' : '#94A3B8' }}
                  />
                  {organization.isActive() ? t('my_org.status_active') : t('my_org.status_inactive')}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-6">
          {/* Badge del plan (D2/D8): morph interactivo o estático según gating. */}
          {organization !== null && (
            canShowUpgrade ? (
              <UpgradePlanBadge
                planName={organization.planName}
                onClick={() => navigate('/pricing?context=upgrade')}
              />
            ) : (
              <Badge
                variant="secondary"
                style={{ fontWeight: 900, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', backgroundColor: '#eff6ff', color: 'var(--color-klonup-sky)', border: '1px solid #dbeafe' }}
              >
                {t('my_org.plan_label', { plan: organization.planName })}
              </Badge>
            )
          )}

          {/* Botón Facturación (D7): navega a la página de Facturación (ruta relativa `billing`,
              regla de subrouter). Gating: solo rol Admin, independiente de si la organización
              está activa o inactiva. */}
          {isAdmin && organization !== null && (
            <button
              type="button"
              className="inline-flex items-center rounded-lg border border-klonup-light px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-widest text-klonup-indigo transition-colors hover:bg-klon-muted"
              onClick={() => navigate('billing')}
            >
              {t('my_org.billing_button')}
            </button>
          )}

          {/* Separador entre "plan/facturación" y "tutoriales/cuenta" — mismo patrón visual que
              el separador de la izquierda entre el logo y la identidad de la organización. */}
          {organization !== null && <div aria-hidden="true" style={{ width: '1px', height: '32px', backgroundColor: '#E5E7EB' }} />}

          {/* Botón prominente solo mientras quedan tutoriales pendientes (invita a hacerlos).
              Cuando el usuario los completa todos, el botón desaparece del header y el acceso
              pasa al dropdown de perfil (acceso secundario para repasarlos). */}
          {!allToursCompleted && (
            <button
              data-tour="open-tutorials"
              onClick={openTutorials}
              style={{
                background: 'none',
                border: '1.5px solid var(--color-klonup-light)',
                borderRadius: '9999px',
                height: '32px',
                padding: '0 14px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 700,
                color: 'var(--color-klonup-indigo)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              title={t('onboarding.menu.button')}
            >
              <span aria-hidden="true">🎓</span>
              {t('onboarding.menu.button')}
            </button>
          )}

          <ProfileDropdown
            user={user}
            logout={logout}
            settingsPath={`${prefix}/settings`}
            showTutorialsItem={allToursCompleted}
            onOpenTutorials={openTutorials}
          />
        </div>

      </div>
    </header>
  );
};

/** Formatea una fecha ISO para el banner (mismo patrón que MyOrganizationPage/AdminOrganizationDetailPage). */
function formatBannerDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

const OrgLayout: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const { organization, reload: reloadOrganization } = useMyOrganization(slug);
  const { show: showToast, toastElement: orgInactiveToast } = useToast();

  // Banner de cambio de plan programado (D11): estado del modal de confirmación de cancelación.
  const [isCancelScheduledChangeOpen, setIsCancelScheduledChangeOpen] = useState(false);
  const [isCancellingScheduledChange, setIsCancellingScheduledChange] = useState(false);

  // Los hooks de tour se llaman SIEMPRE (regla de hooks de React), nunca condicionalmente.
  const welcomeAdminTour = useWelcomeAdminTour();
  const createConnectionTour = useCreateConnectionTour();
  const bmWelcomeTour = useBackupManagerWelcomeTour();
  const bmConnectionDetailTour = useBackupManagerConnectionDetailTour();
  const adminConnectionDetailTour = useAdminConnectionDetailTour();
  const adminCreateTaskTour = useAdminCreateTaskTour();

  // El conjunto de tours visibles en el menú depende del rol.
  // - welcome-admin: auto-disparo en my-organization (primer render).
  // - create-connection: Admin-only, manual desde el menú (navega a requiredPath my-organization).
  // - admin-connection-detail / admin-create-task: viven en el detalle de una conexión. El primero
  //   se auto-dispara al entrar (guarda hasCompleted); ambos se deshabilitan fuera de /connections/:id.
  const tours =
    user?.role === 'admin'
      ? [welcomeAdminTour, createConnectionTour, adminConnectionDetailTour, adminCreateTaskTour]
      : user?.role === 'backupmanager'
        ? [bmWelcomeTour, bmConnectionDetailTour]
        : [];
  const progressAdapter = OnboardingDependencyContainer.getTourProgressAdapter();

  // Etiquetas del chrome del tour resueltas por i18n (el motor del DS es i18n-agnostic).
  const tourLabels: TourLabels = {
    stepOf: (current, total) => t('onboarding.controls.step_of', { current, total }),
    prev: t('onboarding.controls.prev'),
    next: t('onboarding.controls.next'),
    done: t('onboarding.controls.done'),
  };

  const menuLabels: TutorialsMenuLabels = {
    eyebrow: t('onboarding.menu.eyebrow'),
    title: t('onboarding.menu.title'),
    description: t('onboarding.menu.description'),
    allCompleted: t('onboarding.menu.all_completed'),
    progress: (completed, total) => t('onboarding.menu.progress', { completed, total }),
    completedLabel: title => t('onboarding.menu.completed_label', { title }),
  };

  const { isMenuOpen, activeTour, openMenu, closeMenu, startTour, closeTour, hasCompleted, hasSeen } =
    useOnboardingTour({ tours, progressAdapter });

  // Todos los tutoriales del rol completados → el botón prominente del header se oculta y el
  // acceso pasa al dropdown de perfil. Si no hay tours (p.ej. SuperAdmin), no se considera "todos
  // completados" para no mostrar un acceso a un menú vacío.
  const allToursCompleted = tours.length > 0 && tours.every(tour => hasCompleted(tour.id));

  const location = useLocation();
  const navigate = useNavigate();

  // ¿Estamos dentro del detalle de una conexión? (ruta /organization/:slug/connections/:id).
  // Los tours de TOURS_REQUIRING_KLON_DETAIL enseñan esa pantalla y sus anclas (connection-tasks,
  // connection-team, connection-configure-button…) solo existen ahí; fuera no tiene sentido lanzarlos.
  // Como la ruta es dinámica (:id) no se puede usar requiredPath para navegar a un connection
  // concreto → se deshabilitan en el menú.
  const isInsideConnectionDetail = /\/connections\/[^/]+/.test(location.pathname);

  // El motor del DS es agnóstico: recibe qué tours deshabilitar y el texto, no el porqué.
  const isTourDisabled = useCallback(
    (tourId: string): boolean => TOURS_REQUIRING_KLON_DETAIL.includes(tourId) && !isInsideConnectionDetail,
    [isInsideConnectionDetail],
  );
  const tourDisabledHint = useCallback(
    (tourId: string): string | undefined =>
      isTourDisabled(tourId) ? t('onboarding.menu.requires_connection_detail') : undefined,
    [isTourDisabled, t],
  );

  // Tour que espera a que la ruta destino quede activa antes de arrancar (ver startTourWithNav).
  const [pendingTourId, setPendingTourId] = useState<string | null>(null);

  // Compone la ruta absoluta del requiredPath relativo de un tour, o null si no declara requiredPath.
  const resolveTourPath = useCallback(
    (tourId: string): string | null => {
      const tour = tours.find(t => t.id === tourId);
      if (!tour?.requiredPath || !slug) return null;
      return `${orgPaths.root(slug ?? '')}/${tour.requiredPath}`;
    },
    [tours, slug],
  );

  // Arranque real del tour: avisa a la página (evento window) de que un tour va a empezar,
  // para que resetee su layout a estado por defecto (MyOrganizationPage colapsa el panel
  // expandido y cierra drawers abiertos). Luego arranca el tour.
  //
  // El desfase de carga (las anclas no existen mientras la página muestra el spinner) lo
  // absorbe el PRIMER paso de cada tour, que es centrado y sin target (welcome-admin,
  // create-connection, bm-*): mientras el usuario lo lee, el layout se asienta y los datos llegan,
  // de modo que el paso siguiente ya resalta su ancla en la posición definitiva.
  const beginTour = useCallback(
    (tourId: string) => {
      setTourActive(true); // los paneles que se abran durante el tour no roban el foco (sin autofocus)
      window.dispatchEvent(new CustomEvent('klonup:tour-starting', { detail: { tourId } }));
      startTour(tourId);
    },
    [startTour],
  );

  // Punto único de arranque de tours: si el tour declara requiredPath y no estamos ya en esa
  // ruta, navega y difiere el arranque (lo retoma el useEffect de abajo cuando pathname coincide).
  // El motor del DS no navega (es agnóstico del router) — esa responsabilidad vive aquí.
  const startTourWithNav = useCallback(
    (tourId: string) => {
      const targetPath = resolveTourPath(tourId);
      if (!targetPath || location.pathname === targetPath) {
        beginTour(tourId);
        return;
      }
      setPendingTourId(tourId);
      navigate(targetPath);
    },
    [resolveTourPath, location.pathname, beginTour, navigate],
  );

  // Arranca el tour pendiente cuando la ruta destino queda activa, y limpia el flag para no
  // re-disparar en navegaciones posteriores.
  useEffect(() => {
    if (!pendingTourId) return;
    const targetPath = resolveTourPath(pendingTourId);
    if (targetPath && location.pathname === targetPath) {
      beginTour(pendingTourId);
      setPendingTourId(null);
    }
  }, [location.pathname, pendingTourId, resolveTourPath, beginTour]);

  // Listener de org-inactive → toast informativo (emitido por fetchWithInterceptor o api.ts)
  useEffect(() => {
    const handleOrgInactive = () => {
      showToast(t('org_inactive.toast'));
    };
    window.addEventListener('org-inactive', handleOrgInactive);
    return () => window.removeEventListener('org-inactive', handleOrgInactive);
  }, [showToast, t]);

  // Listener de plan-limit-reached → un toast por cada recurso bloqueado (R14)
  useEffect(() => {
    const handlePlanLimitReached = (e: Event) => {
      const blockedBy = (e as CustomEvent<{ blockedBy?: Array<{ resource: string; excessBy: number }> }>).detail
        ?.blockedBy;
      const resources = blockedBy?.length ? blockedBy.map(b => b.resource) : ['connections'];
      resources.forEach(resource => {
        showToast(t(`plan_limit.toast.${resource}`), 'warning');
      });
    };
    window.addEventListener('plan-limit-reached', handlePlanLimitReached);
    return () => window.removeEventListener('plan-limit-reached', handlePlanLimitReached);
  }, [showToast, t]);

  // Escuchar eventos window para abrir menú/iniciar tour
  useEffect(() => {
    const handleOpenMenu = () => openMenu();
    const handleStartTour = (e: Event) => {
      const customEvent = e as CustomEvent<{ tourId: string }>;
      const tourId = customEvent.detail.tourId;
      // Para los tours auto-disparados por evento, respetar la guarda de "visto": un tour
      // cerrado a medias (visto) tampoco debe re-saltar. El relanzamiento manual desde el menú
      // no pasa por aquí (usa onSelect → startTourWithNav).
      if (AUTO_TRIGGERED_BY_EVENT.includes(tourId) && hasSeen(tourId)) {
        return;
      }
      startTourWithNav(tourId);
    };
    window.addEventListener('klonup:open-tutorials-menu', handleOpenMenu);
    window.addEventListener('klonup:start-tour', handleStartTour);
    return () => {
      window.removeEventListener('klonup:open-tutorials-menu', handleOpenMenu);
      window.removeEventListener('klonup:start-tour', handleStartTour);
    };
  }, [openMenu, startTourWithNav, hasSeen]);

  // Auto-disparo del tour de bienvenida en el primer render, según rol.
  // IMPORTANTE: el progreso se precarga del backend de forma asíncrona. Si consultáramos
  // `hasSeen` en el primer render, los Sets aún estarían vacíos y el tour reaparecería SIEMPRE
  // (race). Esperamos a que el adapter esté listo (whenReady) antes de decidir. Si el adapter
  // no expone whenReady (otros adapters), decidimos de inmediato como antes.
  useEffect(() => {
    let cancelled = false;

    const decide = () => {
      if (cancelled) return;
      // Auto-salta una vez: solo si el tour no ha sido visto NI completado (D7). Cerrarlo a medias
      // (visto) evita el re-salto igual que completarlo, pero sin pintar el check verde.
      if (user?.role === 'admin' && !hasSeen('welcome-admin')) {
        startTourWithNav('welcome-admin');
      }
      if (user?.role === 'backupmanager' && !hasSeen('bm-welcome')) {
        startTourWithNav('bm-welcome');
      }
    };

    const ready = (progressAdapter as { whenReady?: () => Promise<void> }).whenReady;
    if (typeof ready === 'function') {
      ready.call(progressAdapter).then(decide);
    } else {
      decide();
    }

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo en el primer render

  if (slug && user?.organizationSlug && slug !== user.organizationSlug) {
    return <Navigate to={orgPaths.myOrganization(user.organizationSlug)} replace />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }}>
      <OrgHeader
        allToursCompleted={allToursCompleted}
        organization={organization}
      />

      {/* Banner de org inactiva: visible cuando la organización está inactiva (R18/R19).
          El mensaje/CTA se deriva del motivo real (inactivityReason) vía inactivityBanner(). */}
      {organization && !organization.isActive() && (() => {
        const banner = inactivityBanner(organization.inactivityReason, organization.planId);
        const isCritical = banner.severity === 'crit';
        const colors = isCritical
          ? { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' }
          : { bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' };

        const handleCtaClick = () => {
          // El CTA de mailto: no navega si el usuario no tiene cliente de correo asociado
          // (sin feedback visible). El email queda mostrado como ContactEmailLink en el
          // cuerpo del mensaje crítico para que siempre haya una vía de contacto tangible.
          if (banner.ctaAction.type === 'navigate') {
            navigate(banner.ctaAction.to);
          }
        };

        return (
          <div
            data-testid="org-inactive-banner"
            style={{
              backgroundColor: colors.bg,
              borderBottom: `1px solid ${colors.border}`,
              padding: '12px 2rem',
            }}
          >
            <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              <div>
                <p style={{ fontWeight: 700, color: colors.text, fontSize: '14px', margin: 0 }}>
                  {t(banner.titleKey)}
                </p>
                <p style={{ color: colors.text, fontSize: '13px', margin: '2px 0 0 0' }}>
                  {t(banner.bodyKey)}
                  {banner.ctaAction.type === 'mailto' && (
                    <>
                      {' '}
                      <ContactEmailLink
                        email={banner.ctaAction.to}
                        className="font-bold underline"
                        style={{ color: colors.text }}
                      />
                    </>
                  )}
                </p>
              </div>
              {banner.ctaAction.type === 'navigate' && (
                <Button
                  variant="gradient"
                  onClick={handleCtaClick}
                  style={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {t(banner.ctaKey)}
                </Button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Banner de cambio de plan programado (D11): mutuamente excluyente con el de org inactiva —
          scheduledPlanChange solo existe con hasActiveSubscription true (org activa); inactivityReason
          solo existe con organización inactiva. La guarda organization.isActive() garantiza la exclusión. */}
      {organization && organization.isActive() && (() => {
        const banner = scheduledPlanChangeBanner(
          organization.scheduledPlanChange,
          organization.scheduledPlanChangeFits,
          organization.scheduledPlanChangeBlockedBy,
        );
        if (!banner || !organization.scheduledPlanChange) return null;

        const { targetPlanName, effectiveAt } = organization.scheduledPlanChange;
        const isWarning = banner.severity === 'warn';
        const colors = isWarning
          ? { bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' }
          : { bg: '#EEF2FF', border: '#C7D2FE', text: '#1E3A8A' };
        const blockedResourcesText = (banner.blockedBy ?? [])
          .map((resource) => t(`pricing.usage.${resource}`, { count: 2 }))
          .join(', ');

        const handleCancelConfirm = async () => {
          setIsCancellingScheduledChange(true);
          try {
            await OrganizationDependencyContainer.getOrganizationRepository().cancelScheduledPlanDowngrade();
            setIsCancelScheduledChangeOpen(false);
            showToast(t('scheduled_plan_change.cancel_success'));
            reloadOrganization();
          } catch {
            showToast(t('scheduled_plan_change.cancel_error'), 'error');
          } finally {
            setIsCancellingScheduledChange(false);
          }
        };

        return (
          <>
            <div
              data-testid="scheduled-plan-change-banner"
              style={{
                backgroundColor: colors.bg,
                borderBottom: `1px solid ${colors.border}`,
                padding: '12px 2rem',
              }}
            >
              <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div>
                  <p style={{ fontWeight: 700, color: colors.text, fontSize: '14px', margin: 0 }}>
                    {t(banner.titleKey)}
                  </p>
                  <p style={{ color: colors.text, fontSize: '13px', margin: '2px 0 0 0' }}>
                    {t(banner.bodyKey, { planName: targetPlanName, date: formatBannerDate(effectiveAt) })}
                  </p>
                  {banner.blockedBy && banner.blockedBy.length > 0 && (
                    <p style={{ color: colors.text, fontSize: '13px', fontWeight: 700, margin: '2px 0 0 0' }}>
                      {t('scheduled_plan_change.quota_warning', { resources: blockedResourcesText })}
                    </p>
                  )}
                </div>
                <Button
                  variant="gradient"
                  onClick={() => setIsCancelScheduledChangeOpen(true)}
                  style={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {t(banner.ctaKey)}
                </Button>
              </div>
            </div>
            <ConfirmModal
              open={isCancelScheduledChangeOpen}
              onClose={() => setIsCancelScheduledChangeOpen(false)}
              onConfirm={handleCancelConfirm}
              title={t('scheduled_plan_change.cancel_confirm_title')}
              description={t('scheduled_plan_change.cancel_confirm_body')}
              confirmLabel={t('scheduled_plan_change.cancel_button')}
              variant="warning"
              loading={isCancellingScheduledChange}
              confirmTestId="scheduled-plan-change-cancel-confirm"
              cancelTestId="scheduled-plan-change-cancel-dismiss"
            />
          </>
        );
      })()}

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2.5rem 2rem', overflowX: 'hidden' }}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner size="md" className="h-96" />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
      <Tour
        steps={activeTour?.steps ?? []}
        open={activeTour !== null}
        labels={tourLabels}
        onStepChange={(step) => {
          // Puente motor→app: emite el paso activo para que la página sincronice su UI (p.ej.
          // admin-connection-detail abre el modo "Configurar" en un paso y debe cerrarlo al retroceder).
          if (activeTour) {
            window.dispatchEvent(
              new CustomEvent('klonup:tour-step', { detail: { tourId: activeTour.id, stepId: step.id } }),
            );
          }
        }}
        onClose={(reason) => {
          // Avisar a la página activa de qué tour se cerró: algunos tours dejan la UI en un
          // estado intermedio (p.ej. admin-connection-detail abre el modo "Configurar" en su último
          // paso) y la página lo revierte al recibir este evento. El motor del DS es agnóstico
          // de la app — OrgLayout hace de puente, igual que con klonup:start-tour.
          const closedTourId = activeTour?.id;
          if (closedTourId) {
            window.dispatchEvent(new CustomEvent('klonup:tour-closed', { detail: { tourId: closedTourId } }));
          }
          setTourActive(false); // el tour terminó: los paneles vuelven a autoenfocar en uso normal
          // 'completed' (pulsó "¡Listo!") → check verde; 'dismissed' (cerró a medias) → solo visto.
          closeTour({ reason });
        }}
      />
      <TutorialsMenu
        tours={tours}
        open={isMenuOpen}
        labels={menuLabels}
        onClose={closeMenu}
        onSelect={(tourId) => startTourWithNav(tourId)}
        hasCompleted={hasCompleted}
        isDisabled={isTourDisabled}
        disabledHint={tourDisabledHint}
      />
      {orgInactiveToast}
    </div>
  );
};

export default OrgLayout;
